/* eslint-disable import/first */
import React, { useEffect, Suspense, lazy } from 'react';
import { retryingImport, clearChunkReloadFlag } from './utils/lazyWithRetry';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import AnimatedOutlet from './components/ui/AnimatedOutlet';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { homePath, FIRST_RUN_DONE_KEY } from './utils/firstRun';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout.jsx';
import { LoginPage } from './components/auth/LoginPage';
// Lazy: this OAuth-callback route statically pulls schoologyAPI -> Firestore,
// which put the whole SDK in the initial bundle for a page almost nobody hits.
const SchoologyCallback = lazy(retryingImport(() =>
  import('./components/auth/SchoologyCallback').then((m) => ({ default: m.SchoologyCallback }))
));
import GuestGate from './components/GuestGate';
import { metaFor } from './constants/routeMeta';
import { canonicalFor, INDEX_ROBOTS } from './utils/pageMeta';
import useDocumentMeta from './hooks/useDocumentMeta';
import { Calendar, FileQuestion, Zap, Calculator, Settings as SettingsIcon, Activity, Brain, TrendingUp, Users } from 'lucide-react';
import ErrorBoundary from './components/ErrorBoundary';
import PageSkeleton from './components/ui/PageSkeleton';
import { ToastProvider } from './contexts/ToastContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import ToastContainer from './components/ui/Toast';
import AiDowngradeNotice from './components/ui/AiDowngradeNotice';
// eslint-disable-next-line import/first
const AITutors = lazy(retryingImport(() => import('./pages/AITutors')));
// eslint-disable-next-line import/first
const SmartScheduler = lazy(retryingImport(() => import('./pages/SmartScheduler')));
// eslint-disable-next-line import/first
const PracticeTests = lazy(retryingImport(() => import('./pages/PracticeTests')));
// eslint-disable-next-line import/first
const Settings = lazy(retryingImport(() => import('./pages/Settings')));
// eslint-disable-next-line import/first
const Flashcards = lazy(retryingImport(() => import('./pages/Flashcards')));
// eslint-disable-next-line import/first
const Solver = lazy(retryingImport(() => import('./pages/Solver')));
// eslint-disable-next-line import/first
const Diagnostics = lazy(retryingImport(() => import('./pages/Diagnostics')));
// eslint-disable-next-line import/first
const Start = lazy(retryingImport(() => import('./pages/Start')));
// eslint-disable-next-line import/first
const StudyPack = lazy(retryingImport(() => import('./pages/StudyPack')));
// eslint-disable-next-line import/first
const StudyIndex = lazy(retryingImport(() => import('./pages/StudyIndex')));
// eslint-disable-next-line import/first
const Timeline = lazy(retryingImport(() => import('./pages/Timeline')));
// eslint-disable-next-line import/first
const Review = lazy(retryingImport(() => import('./pages/Review')));
// eslint-disable-next-line import/first
const Practice = lazy(retryingImport(() => import('./pages/Practice')));
// eslint-disable-next-line import/first
const Legal = lazy(retryingImport(() => import('./pages/Legal')));
// eslint-disable-next-line import/first
const ScoreCalculator = lazy(retryingImport(() => import('./pages/ScoreCalculator')));
// eslint-disable-next-line import/first
const ProgressPage = lazy(retryingImport(() => import('./pages/Progress')));
// eslint-disable-next-line import/first
const Classes = lazy(retryingImport(() => import('./pages/Classes')));
// eslint-disable-next-line import/first
const NotFound = lazy(retryingImport(() => import('./pages/NotFound')));
import { createPageUrl } from './utils/helpers';
import { initializeBackgroundSync } from './services/backgroundSync';
import { initAnalytics, trackPageView } from './utils/analytics';

/**
 * Sets title / description / canonical for routes that do not manage their own.
 *
 * Without this every SPA route kept index.html's head, which canonicalises to
 * the homepage — so Google folded nine distinct pages into one and indexed two
 * pages total. Score-calculator routes return null from metaFor() because
 * ScoreCalculator.jsx is their single writer.
 */
function RouteMeta() {
  const { pathname } = useLocation();
  const meta = metaFor(pathname);
  useDocumentMeta({
    title: meta?.title,
    description: meta?.description,
    // Canonicalise to the path itself. Passing undefined when there is no entry
    // leaves whatever the owning page set, rather than stamping the homepage.
    canonical: meta ? canonicalFor(pathname) : undefined,
    robots: meta ? meta.robots || INDEX_ROBOTS : undefined,
  });
  return null;
}

// Per-feature copy shown to guests on the sign-in upsell (GuestGate).
// AI Tutors is intentionally absent — it's open to guests.
// `heading` and `about` are for the indexed pages: Googlebot is a guest, so
// without them each of these ranked on a 60-word sign-in card.
const FEATURES = {
  scheduler: {
    icon: Calendar, title: 'Smart Scheduler', preview: '/guest-previews/scheduler.jpg',
    heading: 'AP study scheduler with Schoology sync',
    blurb: 'Sign in for free to build an AI study schedule that adapts to your subjects, deadlines, and Schoology assignments.',
    about: [
      'Add your assignments, tests and AP exam dates, or pull them in from Schoology.',
      'The scheduler spreads study sessions across the days before each deadline, inside the hours you choose to study, and skips the times you block off.',
      'When plans change, rebuild the schedule and everything you have not done yet moves to a new slot.',
    ],
  },
  practice: {
    icon: FileQuestion, title: 'Practice Tests', preview: '/guest-previews/practice.jpg',
    heading: 'AP practice tests, generated and scored',
    blurb: 'Sign in for free to generate full-length AP practice tests with timed sections and detailed scoring.',
    about: [
      'Pick a subject and the units you want, and get a practice test in the format of the real AP exam: multiple-choice questions, plus free-response questions for the subjects that have them.',
      'Sections are timed like the real exam. Every answer choice comes with an explanation, and free responses are graded against the scoring rubric, point by point.',
      'Your results become a predicted 1–5 score and a list of the units to work on next. Every question you miss goes into your review queue.',
    ],
  },
  flashcards: {
    icon: Zap, title: 'Flashcards', preview: '/guest-previews/flashcards.jpg',
    heading: 'AP flashcards with spaced repetition',
    blurb: 'Sign in for free to create, study, and share flashcard decks with spaced repetition.',
    about: [
      'Make a deck for any AP class, or import a set you already have from Quizlet.',
      'Spaced repetition schedules every card: the ones you know come back later, the ones you miss come back soon.',
      'Share a deck publicly so classmates can study it, or study decks other students have shared.',
    ],
  },
  solver: {
    icon: Calculator, title: 'Problem Solver', preview: '/guest-previews/solver.jpg',
    heading: 'AP problem solver, step by step',
    blurb: 'Sign in for free to get step-by-step solutions to any problem from a photo or text.',
    about: [
      'Take a photo of a problem or type it in. The solver works through it step by step, so you see the method and not just the answer.',
      'Each solution lists the key concepts it uses and the common mistakes to watch for.',
      'Built for AP math and science: calculus, precalculus, statistics, physics, chemistry and more.',
    ],
  },
  settings:    { icon: SettingsIcon, title: 'Settings',          preview: '/guest-previews/settings.jpg',    blurb: 'Sign in for free to pick your AP subjects, customize your tutor, and manage your account.' },
  diagnostics: { icon: Activity,     title: 'Diagnostics',       preview: '/guest-previews/diagnostics.jpg', blurb: 'Sign in for free to run a diagnostic that pinpoints your strengths and weak spots per subject.' },
  review:      { icon: Brain,        title: 'Review',            blurb: 'Sign in for free to turn every question you miss into a spaced-repetition card that comes back until it sticks.' },
  practiceHub: {
    icon: FileQuestion, title: 'Practice',
    heading: 'AP practice: tests, flashcards and review',
    blurb: 'Sign in for free to take AP practice tests, study flashcards, and review every question you have missed.',
    about: [
      'Practice tests in the format of the real AP exam, scored with a predicted 1–5.',
      'Flashcard decks with spaced repetition, your own or ones other students have shared.',
      'A review queue built from every question you got wrong, so each one comes back until you get it right.',
    ],
  },
  progress:    { icon: TrendingUp,   title: 'Progress',          blurb: 'Sign in for free to track your accuracy, streaks, and measured weak spots across every AP subject.' },
  classes:     { icon: Users,        title: 'Classes',           blurb: 'Sign in for free to join your class or club with a link and compare progress on a shared leaderboard.' },
};

// Main App Component
function App() {
  // The app mounted, so whatever chunk state forced a reload is resolved. Clear
  // the guard now rather than leaving it set for the rest of the session, or the
  // NEXT deploy's stale chunk would throw instead of reloading.
  useEffect(() => { clearChunkReloadFlag(); }, []);

  // Initialize background sync when app starts
  useEffect(() => {
    initializeBackgroundSync();
    // No-op unless REACT_APP_GA_MEASUREMENT_ID is set, so dev and previews
    // never send events.
    initAnalytics();
  }, []);

  return (
    <ThemeProvider>
    <ToastProvider>
    <ConfirmProvider>
    <AuthProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/schoology-callback" element={<Suspense fallback={<PageSkeleton />}><SchoologyCallback /></Suspense>} />
          <Route path="/*" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
        </Routes>
        {/* Sits beside AnalyticsRouteTracker: both are route-change side
            effects that need to run for every page, including /login. */}
        <RouteMeta />
        <AnalyticsRouteTracker />
        <ToastContainer />
        <AiDowngradeNotice />
      </Router>
    </AuthProvider>
    </ConfirmProvider>
    </ToastProvider>
    </ThemeProvider>
  );
}

/**
 * Client-side redirect from an old PascalCase route to its kebab-case
 * equivalent, preserving any subpath.
 *
 * Lives alongside the Netlify _redirects 301s as defense-in-depth:
 *   - Netlify _redirects fires only on initial HTTP requests, so it
 *     misses any client-side react-router navigation. This catches those.
 *   - On localhost (CRA dev server) there's no Netlify layer at all,
 *     so this is the only thing redirecting old URLs.
 *
 * Example: hit `/AITutors/AP%20Statistics` → renders this component
 * with `to="/ai-tutors"` and splat="AP%20Statistics" → Navigate to
 * `/ai-tutors/AP%20Statistics`.
 */
/**
 * /join/:code → /classes/:code. The short link is what teachers actually share,
 * but the class page is where the join happens, so this is a pure redirect.
 */
function LearnTimelineRedirect() {
  const { subject } = useParams();
  return <Navigate to={`/study/timelines/${subject || ''}`} replace />;
}

function JoinRedirect() {
  const { code } = useParams();
  return <Navigate to={`${createPageUrl('Classes')}/${code || ''}`} replace />;
}

/**
 * SPA route changes aren't page loads, so GA never sees them without this.
 * Lives inside the Router so it can use useLocation.
 */
function AnalyticsRouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
}

/**
 * "/" shows the first-run check to anyone who hasn't done it, and sends
 * everyone else to the tutors. Renders nothing until that's known, so a
 * returning student is never shown the check while their profile loads.
 *
 * The check renders IN PLACE rather than redirecting to /start: Google renders
 * "/" as a new visitor, and a redirect made it index the homepage under the
 * /start title and description. Staying on "/" keeps the homepage's own meta.
 */
function HomeRedirect() {
  const { user, isGuest } = useAuth();
  let localDone = false;
  try { localDone = localStorage.getItem(FIRST_RUN_DONE_KEY) === 'true'; } catch { /* private window */ }
  const to = homePath({ user, isGuest, localDone });
  if (to === '/start') return <Start />;
  return to ? <Navigate to={to} replace /> : null;
}

function LegacyRedirect({ to }) {
  const params = useParams();
  const splat = params['*'] || '';
  const target = splat ? `${to}/${splat}` : to;
  return <Navigate to={target} replace />;
}

// Main App with Layout
function MainApp() {
  return (
    <Suspense fallback={<PageSkeleton />}>
      <Routes>
        <Route element={<Layout><ErrorBoundary><Suspense fallback={<PageSkeleton />}><AnimatedOutlet /></Suspense></ErrorBoundary></Layout>}>
          <Route index element={<HomeRedirect />} />
          <Route path="/start" element={<Start />} />
          <Route path="/study" element={<StudyIndex />} />
          <Route path="/study/:subject" element={<StudyIndex />} />
          <Route path="/study/timelines/:subject" element={<Timeline />} />
          <Route path="/study/:subject/:unit" element={<StudyPack />} />
          <Route path={createPageUrl("AITutors")} element={<AITutors />} />
          <Route path={createPageUrl("AITutors", ":subject")} element={<AITutors />} />
          <Route path={createPageUrl("SmartScheduler")} element={<GuestGate feature={FEATURES.scheduler}><SmartScheduler /></GuestGate>} />
          <Route path={createPageUrl("PracticeTests")} element={<GuestGate feature={FEATURES.practice}><PracticeTests /></GuestGate>} />
          <Route path={createPageUrl("Flashcards")} element={<GuestGate feature={FEATURES.flashcards}><Flashcards /></GuestGate>} />
          <Route path={createPageUrl("Solver")} element={<GuestGate feature={FEATURES.solver}><Solver /></GuestGate>} />
          <Route path={createPageUrl("Settings")} element={<GuestGate feature={FEATURES.settings}><Settings /></GuestGate>} />
          <Route path={createPageUrl("Diagnostics")} element={<GuestGate feature={FEATURES.diagnostics}><Diagnostics /></GuestGate>} />
          <Route path={createPageUrl("Diagnostics", ":subject")} element={<GuestGate feature={FEATURES.diagnostics}><Diagnostics /></GuestGate>} />
          <Route path={createPageUrl("Diagnostics", ":subject/start")} element={<GuestGate feature={FEATURES.diagnostics}><Diagnostics /></GuestGate>} />
          {/* Legal pages are intentionally NOT behind GuestGate — they must be
              readable (and crawlable) without an account, and linkable from
              outside the app. */}
          {/* Public: no GuestGate. This is the highest-intent free search query
              in AP prep ("AP Biology score calculator") and gating it would
              defeat the purpose. */}
          <Route path={createPageUrl("ApScoreCalculator")} element={<ScoreCalculator />} />
          <Route path={createPageUrl("ApScoreCalculator", ":slug")} element={<ScoreCalculator />} />
          <Route path={createPageUrl("Privacy")} element={<Legal />} />
          <Route path={createPageUrl("Terms")} element={<Legal />} />
          <Route path={createPageUrl("Practice")} element={<GuestGate feature={FEATURES.practiceHub}><Practice /></GuestGate>} />
          <Route path={createPageUrl("Review")} element={<GuestGate feature={FEATURES.review}><Review /></GuestGate>} />
          <Route path={createPageUrl("Progress")} element={<GuestGate feature={FEATURES.progress}><ProgressPage /></GuestGate>} />
          <Route path={createPageUrl("Classes")} element={<GuestGate feature={FEATURES.classes}><Classes /></GuestGate>} />
          <Route path={createPageUrl("Classes", ":code")} element={<GuestGate feature={FEATURES.classes}><Classes /></GuestGate>} />
          {/* Short share link. Kept separate from /classes/:code so the thing a
              teacher pastes into a slide reads as an invitation, not a page. */}
          <Route path="/join/:code" element={<JoinRedirect />} />
          {/* The old dev-only Learn hub folded into /study, which is now called
              Learn: its timelines live at /study/timelines/:subject. */}
          <Route path="/learn" element={<Navigate to="/study" replace />} />
          <Route path="/learn/timeline/:subject" element={<LearnTimelineRedirect />} />
          <Route path="/learn/*" element={<Navigate to="/study" replace />} />

          {/* Legacy PascalCase routes — redirect to kebab-case canonicals.
              The `/*` splat catches subpaths (e.g., /AITutors/statistics). */}
          <Route path="/AITutors/*"       element={<LegacyRedirect to="/ai-tutors" />} />
          <Route path="/SmartScheduler/*" element={<LegacyRedirect to="/smart-scheduler" />} />
          <Route path="/PracticeTests/*"  element={<LegacyRedirect to="/practice-tests" />} />
          <Route path="/Flashcards/*"     element={<LegacyRedirect to="/flashcards" />} />
          <Route path="/Solver/*"         element={<LegacyRedirect to="/solver" />} />
          <Route path="/Settings/*"       element={<LegacyRedirect to="/settings" />} />
          <Route path="/Diagnostics/*"    element={<LegacyRedirect to="/diagnostics" />} />
        </Route>
        {/* 404 renders full-screen, outside the Layout */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export default App;
